// @givmo/board — EVERY word the compensation decisions add to the web (CBG-YR4-PAY, CBG-YR4-PAY-CONSENT):
// the motion card's "Compensation details" (frame c1) and its part on the card, Compensation
// decisions (frame c2; the Form 990 page's second part since CBG-UX8-MENU), "Add a decision from your
// records", a written consent's compensation panel, and the documents beside a consent's text on the
// director's page.
//
// ══ WHICH WORDS ARE WHOSE ═══════════════════════════════════════════════════════════════════════
//
// The SERVED sentences come off the wire and are printed as served, never retyped: the timing line
// and the regulation's line (`written_down.statement`, `regulation_statement`), where a decision from
// the minute book came from (`provenance_statement`, review-176's P3: printed as served), the board's
// own recorded determination, and every refusal's sentence. The board's are approved, on the
// backend's copy page (`scratchpad/2026-10-15-pay-copy-round/copy-review.html`, CBG-YR4-COPY1,
// 2026-10-16); a committee's, the backend's committee twins, are approved in CBG-YR6-COPY1's next
// part (2026-10-16 11:54 PDT, `scratchpad/2026-10-15-yr6meet-copy-round/copy-review.html`), as is
// the section below that repeats them. A line here that repeats one of them says so ("Served,
// reused").
//
// The frames' words (c1, c2) were drawn by design-151 and approved for building with every word a
// draft (2026-10-15 06:01 PDT); Jonathan approved them as drafted, with the states no frame drew, in
// CBG-YR4-COPY1's next part after that (2026-10-16 11:54 PDT, "go with your recs",
// `scratchpad/2026-10-16-payweb-copy-round/copy-review.html`). Lines approved in earlier rounds are
// reused exactly and marked "Approved, reused".
//
// A committee's decision on Evaluations › Compensation decisions (CBG-FIX87) was approved as built
// in CBG-YR6-COPY1's next part (2026-10-16 19:35 PDT, "go with your recs",
// `scratchpad/2026-10-16-confirm-copy-round/copy-review.html`). That round also replaced the tab's
// lead and the second "How this works" line with the page's proposals, which name a committee's
// decisions beside the board's.
//
// ⚠ The contract's §9 binds every line, and the terminology rail and `compensation-words.test.ts`
// hold it: what the board's own records say, never a verdict on pay. No line supplies, compares or
// judges a figure, says the board's process passes or fails any rule, or answers a Form 990 question:
// line 15a or 15b is the organization's own answer, asked for and shown as such.

/** "1 document" · "2 documents". */
function documents(count: number): string {
  return count === 1 ? '1 document' : `${count} documents`;
}

export const compensationWords = {
  // ── On the motion's card, and its dialog (c1) ─────────────────────────────────────────────────

  // Drawn c1 (the card's menu).
  addDetails: 'Add compensation details',
  // Drawn c1 (the dialog's title), and the card's part and the consent's panel.
  title: 'Compensation details',
  // Drawn c1 ("Carried · Feb 11, 2027").
  carriedOn: (day: string) => `Carried · ${day}`,
  // Drawn c1.
  whosePay: 'Whose pay this sets',
  // Drawn c1.
  nameLabel: 'Name',
  // Drawn c1.
  positionLabel: 'Position, in your words',
  // Drawn c1.
  lineLabel: 'Your Form 990 answer for this person',
  // Drawn c1.
  line15a: 'Part VI, line 15a: the CEO, executive director or top management official',
  // Drawn c1.
  line15b: 'Part VI, line 15b: another officer or key employee',
  // Not drawn: the third choice in the list's "Change your Form 990 answer" only,
  // which takes the answer back to none (review-187's F5).
  lineNeither: 'Neither line 15a nor 15b',
  // Drawn c1.
  lineHint: 'Your organization’s answer. Board records it; it doesn’t decide it.',
  // Drawn c1.
  reliedOnLabel: 'What the board relied on to compare pay',
  // Drawn c1.
  fromPapers: 'From this meeting’s papers.',
  // Not drawn: the meeting has no uploaded papers to choose from.
  noPapers: 'This meeting has no files yet. A file attached on its Packet page can be chosen here.',
  // Drawn c1.
  obtainedLabel: 'How the board obtained it',
  // Drawn c1.
  outsideLabel: 'If the board set pay outside the range in that information, the board’s reason',
  // Drawn c1.
  optional: 'Optional',
  // Drawn c1.
  printsInMinutes: 'These print under the motion in the minutes. Approved minutes never change.',
  // Drawn c1.
  provenance:
    'These follow the items 26 CFR 53.4958-6(c)(3) lists for the records of a board that approves pay. Board records what you enter. It doesn’t judge it.',
  // Shipped, reused (the house's dialog buttons).
  save: 'Save',
  cancel: 'Cancel',
  // Shipped, reused (the consent draft's save failure).
  saveFailed: 'We couldn’t save that. Please try again.',
  // Shipped, reused (the motion card's removal failure).
  removeFailed: 'We couldn’t remove that. Please try again.',
  // Not drawn: a decision saved.
  saved: 'Compensation details saved.',
  // Not drawn: a decision removed.
  removed: 'Compensation details removed.',

  // ── The motion card's part (not drawn) ─────────────────────────────────────────────────────────

  // Not drawn: under a decision's name, how many documents it names.
  documentCount: (count: number) => `${documents(count)} relied on`,
  // Drawn c2 ("Form 990 line 15a (your answer)"), the list's person line.
  yourAnswer: (line: string) => `Form 990 line ${line} (your answer)`,
  // Shipped, reused (the meeting frame's "Edit", a recusal's "Remove").
  edit: 'Edit',
  remove: 'Remove',
  // Not drawn: a decision row's menu, named for a screen reader.
  rowActions: (name: string) => `Actions for ${name}’s compensation details`,
  // Not drawn: the list's act for the organization's own label on a decision.
  changeAnswer: 'Change your Form 990 answer',
  // Not drawn: the label's dialog title.
  answerTitle: 'Your Form 990 answer',
  // Not drawn: the label saved.
  answerSaved: 'Your Form 990 answer is saved.',

  // ── Compensation decisions (c2), the Form 990 page's second part since CBG-UX8-MENU ──────────────

  // Drawn c2 (its tab, then on the page that now shows as Self-assessment); the Form 990 page's
  // second tab since D-CBG-35 (1), the first reading "Part VI" (`form990-words.ts`).
  compensationTab: 'Compensation decisions',
  // Drawn c2; its words replaced in CBG-YR6-COPY1's next part, to name a committee's decisions too.
  listLine:
    'Decisions on pay by your board and its committees, with what their own records say about each.',
  // Drawn c2's "How this works" (the derivation §4.4), rewritten so no line names
  // a word the contract's §9 never says, even to deny it; the second line's words replaced in
  // CBG-YR6-COPY1's next part, to name a committee's decisions too.
  howLines: [
    'A decision appears here once its details are recorded on a carried motion, once a written consent recording them is signed by everyone, or once you add it from your records.',
    'Each part is read from the decision’s own record: the motion, who was there, who was recused, the vote, and what the board or its committee relied on.',
    'Board never supplies pay information, compares figures or judges pay.',
  ] as const,
  // Drawn c2.
  lastDecided: (year: number) => `Last decided ${year}`,
  // Drawn c2.
  theDecision: 'The decision',
  // Drawn c2.
  whoTookPart: 'Who took part',
  // Drawn c2.
  present: 'Present',
  // Approved, reused (the minutes' attendee word, CBG-YR3-ROLES-PRESIDING-COPY1).
  presiding: 'presiding',
  // Approved, reused (CBG-YR3-ROLES's "not a director", alone in the parentheses), drawn c2.
  notADirector: 'not a director',
  // Drawn c2.
  recused: 'Recused',
  // Drawn c2.
  theVote: 'The vote',
  // The minutes' words (approved), after the frame's "The vote:" label: "4 for, 0 against, 0
  // abstaining. Recorded outcome: carried."
  tally: (votesFor: number, against: number, abstaining: number) =>
    `${votesFor} for, ${against} against, ${abstaining} abstaining.`,
  outcome: (outcome: string) => `Recorded outcome: ${outcome}.`,
  // The minutes' words (approved), the line under the board's own determination.
  recordedBasis: (basis: string) => `Recorded basis: ${basis}`,
  // Not drawn: a consent's signers, in place of a meeting's room.
  signedBy: 'Signed by',
  // Drawn c2.
  reliedOn: 'What the board relied on',
  // Drawn c2.
  obtained: 'How it was obtained',
  // Served, reused (the minutes' and the consent block's line, as printed).
  reasonOutside: 'The board’s reason for setting pay outside the range in that information',
  // Drawn c2.
  writtenDown: 'When it was written down',
  // Not drawn: a consent decision's card head ("October 16, 2026 · by written
  // consent").
  byWrittenConsent: 'by written consent',
  // Drawn c2.
  earlier: 'Earlier decisions',
  // Drawn c2.
  addFromRecords: 'Add a decision from your records',
  // The derivation §4.4's empty list.
  empty: 'No compensation decisions are recorded.',
  // Not drawn: the list isn't served here (an older backend, or the module off).
  notServed: 'Compensation decisions aren’t available here yet.',
  // Not drawn: a read failed.
  loadFailed: 'We couldn’t load the compensation decisions just now.',
  // Approved, reused.
  tryAgain: 'Try again',
  // Shipped, reused (the packet's download failure).
  openFailed: 'We couldn’t open that file. Please try again.',

  // ── A decision from the minute book (an index entry; not drawn) ───────────────────────────────

  // Not drawn: an earlier decision's line from the minute book, where the read
  // serves no source sentence.
  recordedIn: (title: string) => `Recorded in ${title}`,
  // Not drawn: where in the record (`record_locator`).
  whereInRecord: 'Where in the record',
  // Shipped, reused (the consent page's link to its filed record).
  openInBook: 'Open it in your minute book',
  // Not drawn: the dialog's lead.
  addLead:
    'Label minutes, a resolution or a written consent already in your minute book as recording the board’s decision on one person’s pay. The record itself doesn’t change.',
  // Not drawn: the record picker.
  recordLabel: 'The record',
  // Not drawn: the picker's prompt.
  recordPlaceholder: 'Choose a record',
  // Not drawn: nothing in the book it could label.
  noRecords:
    'Nothing in your minute book can be labeled yet: approved or imported minutes, resolutions or written consents.',
  // Not drawn: the locator's hint.
  locatorHint: 'Optional. For example, “Page 3, item 4”.',
  // Not drawn: the add.
  add: 'Add',
  // Not drawn: editing one.
  editEntryTitle: 'Change this decision',
  // Not drawn: an index entry added.
  entryAdded: 'Added to the list.',

  // ── A written consent's compensation panel (PAY-CONSENT; not drawn) ───────────────────────────

  // Not drawn: the panel's one line on a draft.
  consentLead: 'If this consent sets someone’s pay: whose, and the documents the board relied on.',
  // Not drawn: the dialog's line on a consent.
  printsInConsent: 'These go out in the consent’s text, and the directors sign them with it.',
  // Not drawn: a decision's documents, uploaded onto it.
  addDocument: 'Add a document',
  // Not drawn: an upload under way.
  uploading: 'Uploading…',
  // Not drawn: a document added and removed.
  documentAdded: (name: string) => `Added ${name}.`,
  documentRemoved: 'Document removed.',
  // Not drawn: a file of a type the upload won't take (the packet's allow-list,
  // in the packet refusal's words but for its "packet").
  wrongType:
    'That kind of file can’t be added. Add a PDF, or a photo or scan as JPEG, PNG, HEIC or TIFF.',
  // Served, reused (409 `board_compensation_document_too_large`), said before an upload the cap
  // would refuse.
  tooLarge: 'Board takes a fingerprint of files up to 100 MB, and this one is larger.',
  // Not drawn: the draft's text doesn't hold the current block exactly — it isn't
  // there, or an earlier block's lines are (act tone; review-187's F3).
  blockMissing: 'The consent’s text doesn’t match these details yet.',
  // Not drawn: no details are left, but the draft's text still holds a block (act
  // tone; review-187's F2). The way out is the text card's own "Edit the wording".
  blockLeft:
    'The consent’s text still carries compensation details. Take them out with “Edit the wording”.',
  // The contract's §20: the officer's act that places the served block.
  addToText: 'Add to the consent’s text',
  // Not drawn: the block placed.
  blockAdded: 'Added to the consent’s text.',
  // Not drawn: the block is in the draft's text.
  blockInText: 'In the consent’s text, as the directors will read it.',
  // Not drawn: the consent has gone out.
  goneOut: 'These went out in the consent’s text and stand as they were sent.',

  // ── On the director's consent page (not drawn) ─────────────────────────────────────────────────

  // Served, reused (the block's own heading line, without its colon).
  documentsRelied: 'Documents the board relied on',
  // Not drawn: the card's one line.
  documentsLine:
    'The files the consent’s text names, each as it was when Board took its fingerprint.',
  // Served, reused (the block's "For:" line).
  forPerson: (name: string, position: string) => `For: ${name}, ${position}`,

  // ── A committee's decision (CBG-YR6-MEET, -CONSENT; review-188's merge note (a)) ─────────────────
  // Not drawn: the lines above that name the board or its directors, for a committee meeting's motion
  // or a committee's written consent — the committee where the board's say the board, as its printed
  // block names it (backend `board_compensation.py`'s committee lines). Approved in CBG-YR6-COPY1's
  // next part (2026-10-16).

  // `reliedOnLabel`, the committee's.
  committeeReliedOnLabel: 'What the committee relied on to compare pay',
  // `obtainedLabel`, the committee's.
  committeeObtainedLabel: 'How the committee obtained it',
  // `outsideLabel`, the committee's.
  committeeOutsideLabel:
    'If the committee set pay outside the range in that information, the committee’s reason',
  // `printsInConsent`, the committee's members.
  committeePrintsInConsent:
    'These go out in the consent’s text, and the committee’s members sign them with it.',
  // `consentLead`, the committee's.
  committeeConsentLead:
    'If this consent sets someone’s pay: whose, and the documents the committee relied on.',
  // `blockInText`, the committee's members.
  committeeBlockInText: 'In the consent’s text, as the committee’s members will read it.',
  // Served, reused (a committee's block's own heading line, without its colon).
  committeeDocumentsRelied: 'Documents the committee relied on',

  // ── Compensation decisions on a committee's decision (CBG-FIX87; not drawn) ─────────────────────
  // The list's card on a decision a committee took names the committee where the board's names the
  // board, from the read's `committee` (as the decision's own record names it, never the registry's
  // name now). Approved in CBG-YR6-COPY1's next part (2026-10-16 19:35 PDT).

  // Not drawn: `reliedOn`, the committee's.
  committeeReliedOn: 'What the committee relied on',
  // Served, reused (a committee's printed block line, as printed): `reasonOutside`, the committee's.
  committeeReasonOutside:
    'The committee’s reason for setting pay outside the range in that information',
  // Not drawn: the card's head on a committee's decision ("Compensation Committee · Pay review").
  committeeHead: (committee: string, title: string) => `${committee} · ${title}`,
} as const;
